"use client";

import { Comedian, FOLLOW_LOCATIONS } from "@punchup/data/legacy";
import { Mail, Phone } from "lucide-react";
import { PoliciesFooter, PolicyType } from "../policiesfooter";
import React, {
  FormHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UserEvents, trackEvent } from "@punchup/analytics";

import { AsYouType } from "libphonenumber-js/max";
import { CountryCode } from "../common/phone-input/types";
import { Database } from "@punchup/data/supabase.types";
import FacebookSignInButton from "./FacebookSignInButton";
import GoogleSignInButton from "./GoogleSignInButton";
import Loader from "../common/icons/Loader";
import PhoneInput from "../common/phone-input";
import PostalCodeFields from "../common/PostalCodeFields";
import SignInCodeForm from "../../components/signin/SignInCodeForm";
import { Provider as SupabaseAuthProvider } from "@supabase/supabase-js";
import { VenuePagesRow } from "@punchup/data/legacy";
import { cn } from "@punchup/utils/cn";
import { createPendingFollow } from "@punchup/data/client";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { genWebAppURL } from "@punchup/utils";
import { getDefaultCountry } from "../common/phone-input/utils";
import { getUserZipcodeOrFallbackToIp } from "@punchup/data/legacy";
import { logException } from "@punchup/sentry";

export enum SignInVariant {
  EMAIL = "email",
  PHONE = "phone",
  GOOGLE = "google",
  FACEBOOK = "facebook",
}

export type SignInFormVariant = "normal" | "minimal";

/**
 * This form is used for both Sign in and Sign up
 * The flow for each of these cases is currently identical
 * If the user does not have an account, then one will be created for them and they will be emailed a code to sign in
 * If the user already has an account, then they will be emailed a code to sign in
 */

interface SignInSignUpFormProps {
  redirectTo?: string | null;
  /**
   * When true, brand-new accounts are routed through the suggested-comedians
   * onboarding stepper (`/suggested`) after signing up, before continuing to
   * `redirectTo`. Returning users go straight to `redirectTo`. Opt-in so only
   * the comedian "guest list" signup forms trigger onboarding.
   */
  routeNewUserToOnboarding?: boolean;
  buttonText?: string;
  comedianToFollow?: (Pick<Comedian, "id" | "slug"> | Pick<Comedian, "slug">) &
    Partial<Pick<Comedian, "display_name_short">>;
  followLocation?: FOLLOW_LOCATIONS;
  ctaSource?: string;
  pageSection?: string;
  venueToFollow?: Pick<VenuePagesRow, "id" | "slug">;
  policyFooter?: React.ReactNode;
  policyFooterType?: PolicyType;
  className?: string;
  postalCodeRequired?: boolean;
  variant?: SignInFormVariant;
  /** Style of the email/phone toggle: 'tabs' (segmented control) or 'radio' (radio buttons) */
  toggleVariant?: "tabs" | "radio";
  /** Layout of the submit + OAuth buttons: 'vertical' (stacked with divider) or 'horizontal' (inline row) */
  buttonLayout?: "vertical" | "horizontal";
  autoFocus?: boolean;
  attendanceData?: {
    showId: string;
    venueId: string;
    comedianId: string;
  };
  isModal?: boolean;
  userEmail?: string;
  handleAuthComplete?: () => Promise<void>;
  callback?: () => void;
}

export default function SignInSignUpForm({
  redirectTo = "/", // TODO: check which usages actually need "/", we must remove this
  routeNewUserToOnboarding = false,
  buttonText = "Continue",
  comedianToFollow,
  followLocation,
  ctaSource,
  pageSection,
  venueToFollow,
  policyFooter,
  policyFooterType = "default",
  className,
  postalCodeRequired = false,
  variant = "normal",
  toggleVariant = "tabs",
  buttonLayout = "vertical",
  autoFocus = false,
  attendanceData,
  isModal = false,
  userEmail,
  handleAuthComplete,
  callback,
}: SignInSignUpFormProps) {
  const [email, setEmail] = useState(userEmail || "");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountry, setSelectedCountry] =
    useState<CountryCode>(getDefaultCountry());
  const [signInMethod, setSignInMethod] = useState<"email" | "phone">("email");

  // Resolve "comedian" policy footer type to email/phone variant based on sign-in method
  const effectivePolicyFooterType = useMemo((): PolicyType => {
    const resolve = (type: string) =>
      type === "comedian"
        ? signInMethod === "phone"
          ? "comedian-phone"
          : "comedian-email"
        : type;

    if (Array.isArray(policyFooterType)) {
      return [
        resolve(policyFooterType[0]),
        resolve(policyFooterType[1]),
      ] as PolicyType;
    }
    return resolve(policyFooterType as string) as PolicyType;
  }, [policyFooterType, signInMethod]);

  const [errors, setErrors] = useState<{
    postalCode?: string;
    countryCode?: string;
    [SignInVariant.EMAIL]?: string;
    [SignInVariant.PHONE]?: string;
    [SignInVariant.GOOGLE]?: string;
    [SignInVariant.FACEBOOK]?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const [displayCodeForm, setDisplayCodeForm] = useState(false);
  const [isInstagramIAB, setIsInstagramIAB] = useState(false);
  // form state
  const [enteredPostalCode, setEnteredPostalCode] = useState<string>("");
  const [enteredCountryCode, setEnteredCountryCode] = useState<string | null>(
    "US" // default to US
  );

  const emailInputRef = useRef<HTMLInputElement>(null);
  const postalCodeInputRef = useRef<HTMLInputElement>(null);
  const supabase = createClientComponentClient<Database>({});
  const isUS = useMemo(() => {
    if (enteredCountryCode) {
      return enteredCountryCode === "US";
    }
    return false;
  }, [enteredCountryCode]);

  // focus relevant input on mount
  useEffect(() => {
    if (!autoFocus) return;
    if (postalCodeRequired) {
      postalCodeInputRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
  }, [autoFocus, postalCodeRequired]);

  // retrieve user's country & postal code on mount
  useEffect(() => {
    async function fetchUserLocation() {
      const { country_code, zipcode } =
        await getUserZipcodeOrFallbackToIp(supabase);
      if (country_code) {
        setEnteredCountryCode(country_code);
        setSelectedCountry((prev) => ({
          ...prev,
          code: country_code as CountryCode["code"],
        }));
      }
      if (zipcode) {
        setEnteredPostalCode(zipcode);
      }
    }

    if (postalCodeRequired) {
      fetchUserLocation();
    }
  }, [postalCodeRequired, supabase]);

  useEffect(() => {
    const isInstagramIAB = navigator?.userAgent.includes("Instagram");
    setIsInstagramIAB(isInstagramIAB);
  }, []);

  const handleCountryChange = (country: CountryCode) => {
    setSelectedCountry(country);
    setEnteredCountryCode(country.code);
  };

  //* Render input that lets user select email vs. sms
  /*
   * Email:
   * Sign the user in (if an account exists with the specified email)
   * -OR-
   * Create a new account (if one doesn't exist with the specified email)
   */

  /*
   * Phone:
   * Check if the phone number exists in the database.  If it does, sign the user in.
   * If it doesn't then we prompt user with messaging opt-in language.
   */
  const renderAuthInput = () => {
    if (signInMethod === "email") {
      return (
        <div className="flex flex-col">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <input
            ref={emailInputRef}
            id="email"
            name="email"
            type="email"
            placeholder={
              variant === "minimal"
                ? "Enter email address"
                : "e.g. alex@gmail.com"
            }
            autoComplete="email"
            disabled={loading}
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            className={cn(
              "w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px]",
              variant === "minimal"
                ? "h-[46px] px-3 py-2.5"
                : "h-[50px] px-4 py-3"
            )}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <label htmlFor="phone" className="sr-only">
          Phone Number
        </label>

        <div className={cn(variant === "minimal" ? "h-[46px]" : "h-[50px]")}>
          <PhoneInput
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            onCountryChange={handleCountryChange}
            variant={variant === "minimal" ? "minimal" : "default"}
          />
        </div>
      </div>
    );
  };
  const signInWithOtp: FormHTMLAttributes<HTMLFormElement>["action"] = async (
    formData
  ) => {
    setErrors({});

    // which "submit" button was clicked?
    const signInType = formData.get("sign-in-type") as SignInVariant;
    const validationErrors: typeof errors = {};

    // if the form requires a postal code and country code
    if (postalCodeRequired) {
      // validate postal code input
      if (isUS) {
        //US Zip Code
        if (!enteredPostalCode) {
          validationErrors.postalCode = "ZIP code is required";
        } else if (enteredPostalCode.length !== 5) {
          validationErrors.postalCode = "ZIP code must be 5 digits";
        }
      } else {
        // non-US postal code
        if (!enteredPostalCode) {
          validationErrors.postalCode = "Postal code is required";
        }
      }
      // validate country code input
      if (!enteredCountryCode) {
        validationErrors.countryCode = "Country is required";
      }
    }
    // if user signs in with email, validate email input value
    if (signInType === SignInVariant.PHONE && signInMethod === "phone") {
      if (!phoneNumber || phoneNumber.length < 1) {
        validationErrors.phone = "Phone number is required";
      } else {
        const asYouType = new AsYouType(selectedCountry.code);
        asYouType.input(phoneNumber);
        if (!asYouType.isValid()) {
          validationErrors.phone = "Please enter a valid phone number";
        }
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      // end form submission and display any validation errors
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    const submissionErrors: typeof errors = {};

    switch (signInType) {
      case SignInVariant.EMAIL:
        const { error: emailError } = await supabase.auth.signInWithOtp({
          email,
        });

        if (emailError) {
          logException(emailError, {
            data: `An error occurred while attempting to sign in with email`,
          });
          trackEvent(UserEvents.OTP_SIGN_IN_FAILED, {
            message: emailError.message,
          });
          // update error to display in the form
          submissionErrors[SignInVariant.EMAIL] = emailError.message;
        } else {
          trackEvent(UserEvents.PASSCODE_REQUESTED, {
            type: "email",
          });
          if (comedianToFollow) {
            createPendingFollow({
              email,
              followType: "comedian",
              targetId:
                "id" in comedianToFollow ? comedianToFollow.id : undefined,
              targetSlug: comedianToFollow.slug,
              followLocation,
            }).catch(logException);
          }
          if (venueToFollow) {
            createPendingFollow({
              email,
              followType: "venue",
              targetId: venueToFollow.id,
              followLocation,
            }).catch(logException);
          }
        }
        setDisplayCodeForm(true);
        break;

      case SignInVariant.PHONE:
        const { error: phoneError } = await supabase.auth.signInWithOtp({
          phone: phoneNumber,
        });
        if (phoneError) {
          logException(phoneError, {
            data: `An error occurred while attempting to sign in with phone`,
          });
          trackEvent(UserEvents.OTP_SIGN_IN_FAILED, {
            message: phoneError.message,
          });
          submissionErrors[SignInVariant.PHONE] = phoneError.message;
        } else {
          trackEvent(UserEvents.PASSCODE_REQUESTED, {
            type: "phone",
          });
          if (comedianToFollow) {
            createPendingFollow({
              phone: phoneNumber,
              followType: "comedian",
              targetId:
                "id" in comedianToFollow ? comedianToFollow.id : undefined,
              targetSlug: comedianToFollow.slug,
              followLocation,
            }).catch(logException);
          }
          if (venueToFollow) {
            createPendingFollow({
              phone: phoneNumber,
              followType: "venue",
              targetId: venueToFollow.id,
              followLocation,
            }).catch(logException);
          }
        }
        setDisplayCodeForm(true);
        break;
      case SignInVariant.GOOGLE:
        const googleProvider: SupabaseAuthProvider = SignInVariant.GOOGLE;
        trackEvent(UserEvents.OAUTH_SIGN_IN_ATTEMPT, { type: googleProvider });

        if (typeof window !== "undefined") {
          window.localStorage.setItem("prevent_storage_clear", "true");
        }

        const googleRedirectUrl = new URL(genWebAppURL("/oauth/redirect"));
        googleRedirectUrl.searchParams.set("src", googleProvider);
        if (redirectTo) {
          googleRedirectUrl.searchParams.set("redirectTo", redirectTo);
        }
        if (routeNewUserToOnboarding) {
          googleRedirectUrl.searchParams.set("routeNewUserToOnboarding", "1");
        }
        if (comedianToFollow) {
          googleRedirectUrl.searchParams.set(
            "comedianToFollow",
            comedianToFollow.slug
          );
        }
        if (followLocation) {
          googleRedirectUrl.searchParams.set("followLocation", followLocation);
        }
        if (venueToFollow) {
          googleRedirectUrl.searchParams.set(
            "venueToFollow",
            venueToFollow.slug
          );
        }
        if (enteredPostalCode) {
          googleRedirectUrl.searchParams.set("postalCode", enteredPostalCode);
        }
        if (enteredCountryCode) {
          googleRedirectUrl.searchParams.set("countryCode", enteredCountryCode);
        }

        // Incluir datos de pago en la URL de redirección si están presentes en localStorage
        if (typeof window !== "undefined") {
          const clientSecret = window.localStorage.getItem(
            "paymentClientSecret"
          );
          const paymentMethodId =
            window.localStorage.getItem("paymentMethodId");
          const subscriptionId = window.localStorage.getItem("subscriptionId");
          const paymentEmail = window.localStorage.getItem("paymentEmail");
          const paymentIntentId =
            window.localStorage.getItem("paymentIntentId");

          if (clientSecret) {
            googleRedirectUrl.searchParams.set("clientSecret", clientSecret);
          }
          if (paymentMethodId) {
            googleRedirectUrl.searchParams.set(
              "paymentMethodId",
              paymentMethodId
            );
          }
          if (subscriptionId) {
            googleRedirectUrl.searchParams.set(
              "subscriptionId",
              subscriptionId
            );
          }
          if (paymentEmail) {
            googleRedirectUrl.searchParams.set("paymentEmail", paymentEmail);
          }
          if (paymentIntentId) {
            googleRedirectUrl.searchParams.set(
              "paymentIntentId",
              paymentIntentId
            );
          }
        }

        const { error: googleError } = await supabase.auth.signInWithOAuth({
          provider: googleProvider,
          options: {
            ...(!isModal && redirectTo
              ? { redirectTo: googleRedirectUrl.toString() }
              : {}),
          },
        });

        if (googleError) {
          // track the error
          logException(googleError, {
            data: `An error occurred while attempting to sign in with ${googleProvider}`,
          });
          trackEvent(UserEvents.OAUTH_SIGN_IN_FAILED, {
            type: googleProvider,
            message: googleError.message,
          });

          // update error to display in the form
          submissionErrors[SignInVariant.GOOGLE] = googleError.message;
        }
        break;
      case SignInVariant.FACEBOOK:
        const facebookProvider: SupabaseAuthProvider = SignInVariant.FACEBOOK;
        trackEvent(UserEvents.OAUTH_SIGN_IN_ATTEMPT, {
          type: facebookProvider,
        });

        // We need to prepend the origin to the redirect URL so that
        // it matches supabase's redirectUrls
        const facebookRedirectUrl = new URL(genWebAppURL("/oauth/redirect"));
        facebookRedirectUrl.searchParams.set("src", facebookProvider);
        if (redirectTo) {
          facebookRedirectUrl.searchParams.set("redirectTo", redirectTo);
        }
        if (routeNewUserToOnboarding) {
          facebookRedirectUrl.searchParams.set("routeNewUserToOnboarding", "1");
        }

        // append comedian slug if specified
        if (comedianToFollow) {
          facebookRedirectUrl.searchParams.set(
            "comedianToFollow",
            comedianToFollow.slug
          );
        }

        // append follow location if specified
        if (followLocation) {
          facebookRedirectUrl.searchParams.set(
            "followLocation",
            followLocation
          );
        }

        // append venue slug if specified
        if (venueToFollow) {
          facebookRedirectUrl.searchParams.set(
            "venueToFollow",
            venueToFollow.id
          );
        }

        // append postal code and country code if necessary
        if (postalCodeRequired) {
          if (enteredPostalCode) {
            facebookRedirectUrl.searchParams.set(
              "postalCode",
              enteredPostalCode
            );
          }
          if (enteredCountryCode) {
            facebookRedirectUrl.searchParams.set(
              "countryCode",
              enteredCountryCode
            );
          }
        }

        const { error: facebookError } = await supabase.auth.signInWithOAuth({
          provider: facebookProvider,
          options: {
            ...(!isModal && redirectTo
              ? { redirectTo: facebookRedirectUrl.toString() }
              : {}),
          },
        });

        if (facebookError) {
          // track the error
          logException(facebookError, {
            data: `An error occurred while attempting to sign in with ${facebookProvider}`,
          });
          trackEvent(UserEvents.OAUTH_SIGN_IN_FAILED, {
            type: facebookProvider,
            message: facebookError.message,
          });

          // update error to display in the form
          submissionErrors[SignInVariant.FACEBOOK] = facebookError.message;
        }
        break;
      default:
        // this should never happen
        const errorMessage = `Invalid sign in type: ${signInType}`;
        logException(errorMessage);
        break;
    }

    if (Object.keys(submissionErrors).length > 0) {
      // end form submission and display any submission errors
      setErrors(submissionErrors);
      setLoading(false);
      return;
    }

    setLoading(false);
  };
  const resetForm = () => {
    setDisplayCodeForm(false);
    setErrors({});
    setEmail("");
    setPhoneNumber("");
    setEnteredPostalCode("");
    setEnteredCountryCode(null);
  };

  return (
    <div className="w-full max-w-[432px] mx-auto">
      {!displayCodeForm ? (
        <form
          className={cn(
            "flex flex-col gap-y-2 border-black border-opacity-10",
            className
          )}
          id="auth-otp"
          action={signInWithOtp}
          onSubmit={() => setLoading(true)}
        >
          <div className="flex flex-col">
            {postalCodeRequired && toggleVariant === "tabs" && (
              <div className="mb-2">
                <PostalCodeFields
                  postalCode={enteredPostalCode}
                  defaultCountryCode={enteredCountryCode}
                  onPostalCodeChange={(value) => setEnteredPostalCode(value)}
                  onCountryCodeChange={(value: string) =>
                    setEnteredCountryCode(value)
                  }
                  postalCodeInputRef={postalCodeInputRef}
                  isUS={isUS}
                  errors={{
                    ...(errors.postalCode
                      ? { postalCode: errors.postalCode }
                      : {}),
                    ...(errors.countryCode
                      ? { country: errors.countryCode }
                      : {}),
                  }}
                  variant={variant}
                />
              </div>
            )}
            {toggleVariant === "tabs" && (
              <div
                className={cn(
                  "flex w-full rounded-md border border-[#C7C8C9] bg-white p-0.5",
                  variant === "minimal" ? "h-[38px] mb-2" : "h-[46px] mb-2"
                )}
              >
                <button
                  type="button"
                  onClick={() => setSignInMethod("email")}
                  className={cn(
                    "flex h-full flex-1 items-center justify-center gap-2 rounded-md transition-all duration-200",
                    signInMethod === "email"
                      ? "bg-[#EAEAEA] text-primary-blue"
                      : "bg-white text-secondary-blue"
                  )}
                >
                  <Mail
                    className={cn(
                      "stroke-[1.5]",
                      variant === "minimal" ? "h-4 w-4" : "h-5 w-5"
                    )}
                  />
                  <span
                    className={cn(
                      "font-normal",
                      variant === "minimal" ? "text-xs" : "text-sm"
                    )}
                  >
                    Email
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setSignInMethod("phone")}
                  className={cn(
                    "flex h-full flex-1 items-center justify-center gap-2 rounded-md transition-all duration-200",
                    signInMethod === "phone"
                      ? "bg-[#EAEAEA] text-primary-blue"
                      : "bg-white text-secondary-blue"
                  )}
                >
                  <Phone
                    className={cn(
                      "stroke-[1.5]",
                      variant === "minimal" ? "h-4 w-4" : "h-5 w-5"
                    )}
                  />
                  <span
                    className={cn(
                      "font-normal",
                      variant === "minimal" ? "text-xs" : "text-sm"
                    )}
                  >
                    Phone number
                  </span>
                </button>
              </div>
            )}

            {toggleVariant === "radio" && (
              <div className="flex items-center gap-4 mb-3.5 md:mb-4">
                <button
                  type="button"
                  onClick={() => setSignInMethod("email")}
                  className="flex items-center gap-x-2 group"
                >
                  <span
                    className={cn(
                      "flex items-center justify-center w-5 h-5 rounded-full border-2",
                      signInMethod === "email"
                        ? "border-primary"
                        : "border-[#C7C8C9]"
                    )}
                  >
                    {signInMethod === "email" && (
                      <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </span>
                  <div className="flex items-center gap-x-1">
                    <Mail
                      strokeWidth={2.5}
                      className={cn(
                        "h-4 w-4",
                        signInMethod === "email"
                          ? "text-primary"
                          : "text-secondary-blue"
                      )}
                    />
                    <span
                      className={cn(
                        "text-sm font-medium",
                        signInMethod === "email"
                          ? "text-primary"
                          : "text-secondary-blue"
                      )}
                    >
                      Email
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSignInMethod("phone")}
                  className="flex items-center gap-x-2 group"
                >
                  <span
                    className={cn(
                      "flex items-center justify-center w-5 h-5 rounded-full border-2",
                      signInMethod === "phone"
                        ? "border-primary"
                        : "border-[#C7C8C9]"
                    )}
                  >
                    {signInMethod === "phone" && (
                      <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </span>
                  <div className="flex items-center gap-x-1">
                    <Phone
                      strokeWidth={2.5}
                      className={cn(
                        "h-4 w-4",
                        signInMethod === "phone"
                          ? "text-primary"
                          : "text-secondary-blue"
                      )}
                    />
                    <span
                      className={cn(
                        "text-sm font-medium",
                        signInMethod === "phone"
                          ? "text-primary"
                          : "text-secondary-blue"
                      )}
                    >
                      Phone number
                    </span>
                  </div>
                </button>
              </div>
            )}

            {renderAuthInput()}

            {postalCodeRequired && toggleVariant === "radio" && (
              <div className="mt-2 md:mt-3">
                <PostalCodeFields
                  postalCode={enteredPostalCode}
                  defaultCountryCode={enteredCountryCode}
                  onPostalCodeChange={(value) => setEnteredPostalCode(value)}
                  onCountryCodeChange={(value: string) =>
                    setEnteredCountryCode(value)
                  }
                  postalCodeInputRef={postalCodeInputRef}
                  isUS={isUS}
                  errors={{
                    ...(errors.postalCode
                      ? { postalCode: errors.postalCode }
                      : {}),
                    ...(errors.countryCode
                      ? { country: errors.countryCode }
                      : {}),
                  }}
                  variant={variant}
                />
              </div>
            )}

            {(errors[SignInVariant.EMAIL] || errors[SignInVariant.PHONE]) && (
              <p className="mt-1 text-xs text-red-600">
                {errors[SignInVariant.EMAIL] || errors[SignInVariant.PHONE]}
              </p>
            )}

            {/* Vertical layout: submit button, then divider, then OAuth buttons */}
            {buttonLayout === "vertical" && (
              <>
                <button
                  type="submit"
                  name="sign-in-type"
                  value={
                    signInMethod === "email"
                      ? SignInVariant.EMAIL
                      : SignInVariant.PHONE
                  }
                  disabled={
                    (signInMethod === "email"
                      ? email.length < 1
                      : phoneNumber.length < 1) || loading
                  }
                  aria-disabled={loading ? "true" : "false"}
                  className={cn(
                    "flex items-center justify-center w-full font-semibold text-white bg-primary rounded-lg shadow-md leading-5 disabled:opacity-50 transition-colors duration-200 ease-in-out relative",
                    variant === "minimal"
                      ? "py-2.5 text-[15px]"
                      : "py-[15px] text-[16px]",
                    variant === "normal" && postalCodeRequired ? "mt-3" : "mt-2"
                  )}
                >
                  <div
                    className={cn(
                      "absolute inset-0 flex items-center justify-center",
                      loading ? "opacity-100" : "opacity-0"
                    )}
                  >
                    <Loader />
                  </div>
                  <React.Fragment key="button_text">
                    <span className={cn(loading ? "opacity-0" : "opacity-100")}>
                      {buttonText}
                    </span>
                  </React.Fragment>
                </button>
              </>
            )}
          </div>

          {/* Vertical layout: "or continue with" divider and OAuth buttons below.
              Skipped for isModal, which hides the OAuth buttons the divider
              introduces - the label alone would leave an empty double rule. */}
          {buttonLayout === "vertical" && !attendanceData && !isModal && (
            <div
              className={cn(
                "flex flex-row items-center justify-center",
                variant === "minimal" ? "my-1" : "my-3 md:my-4"
              )}
            >
              <hr className="flex-1 border-[#E7E7E7] border-t-1" />

              <div
                className={cn(
                  "mx-3 text-[#787B7E] font-sans font-normal",
                  variant === "minimal" ? "text-xs md:text-sm" : "text-sm"
                )}
              >
                or continue with
              </div>

              <hr className="flex-1 border-[#E7E7E7] border-t-1" />
            </div>
          )}
          {buttonLayout === "vertical" && (
            <div
              className={cn(
                "flex-row flex items-center justify-center w-full",
                variant === "minimal" ? "gap-x-3" : "gap-x-6"
              )}
            >
              {!isInstagramIAB && !attendanceData && !isModal && (
                <GoogleSignInButton
                  name="sign-in-type"
                  value={SignInVariant.GOOGLE}
                  variant={variant}
                />
              )}
              {!attendanceData && !isModal && (
                <FacebookSignInButton
                  name="sign-in-type"
                  value={SignInVariant.FACEBOOK}
                  variant={variant}
                />
              )}
            </div>
          )}

          {/* Horizontal layout: OAuth buttons and submit in a single row */}
          {buttonLayout === "horizontal" && (
            <div
              className={cn(
                "flex-row flex items-center justify-center w-full",
                variant === "minimal" ? "gap-x-2.5" : "gap-x-6"
              )}
            >
              {!isInstagramIAB && !attendanceData && !isModal && (
                <GoogleSignInButton
                  name="sign-in-type"
                  value={SignInVariant.GOOGLE}
                  variant={variant}
                  className="md:w-[72px]"
                />
              )}
              {!attendanceData && !isModal && (
                <FacebookSignInButton
                  name="sign-in-type"
                  value={SignInVariant.FACEBOOK}
                  variant={variant}
                  className="md:w-[72px]"
                />
              )}
              <button
                type="submit"
                name="sign-in-type"
                value={
                  signInMethod === "email"
                    ? SignInVariant.EMAIL
                    : SignInVariant.PHONE
                }
                disabled={
                  (signInMethod === "email"
                    ? email.length < 1
                    : phoneNumber.length < 1) || loading
                }
                aria-disabled={loading ? "true" : "false"}
                className={cn(
                  "flex items-center justify-center flex-1 w-full font-semibold text-white bg-primary rounded-lg shadow-md leading-5 disabled:opacity-50 transition-colors duration-200 ease-in-out relative",
                  variant === "minimal"
                    ? "py-2.5 text-[15px]"
                    : "py-[15px] text-[16px]"
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 flex items-center justify-center",
                    loading ? "opacity-100" : "opacity-0"
                  )}
                >
                  <Loader />
                </div>
                <React.Fragment key="button_text">
                  <span className={cn(loading ? "opacity-0" : "opacity-100")}>
                    {buttonText}
                  </span>
                </React.Fragment>
              </button>
            </div>
          )}
          {errors[SignInVariant.FACEBOOK] && (
            <p className="mt-1 text-xs text-red-600">
              {errors[SignInVariant.FACEBOOK]}
            </p>
          )}
          {errors[SignInVariant.GOOGLE] && (
            <p className="mt-1 text-xs text-red-600">
              {errors[SignInVariant.GOOGLE]}
            </p>
          )}
          {variant === "normal" &&
            (policyFooter ? (
              policyFooter
            ) : (
              <PoliciesFooter
                type={effectivePolicyFooterType}
                comedian={comedianToFollow?.display_name_short}
                className={
                  variant === "normal" && postalCodeRequired ? "mt-3" : "mt-2"
                }
              />
            ))}
          {variant === "minimal" &&
            (policyFooter ? (
              policyFooter
            ) : (
              <PoliciesFooter
                type={effectivePolicyFooterType}
                comedian={comedianToFollow?.display_name_short}
                className="mt-0.5 text-[10px]"
              />
            ))}
        </form>
      ) : (
        <SignInCodeForm
          email={email}
          phone={phoneNumber}
          signInMethod={signInMethod}
          comedianToFollow={comedianToFollow}
          followLocation={followLocation}
          ctaSource={ctaSource}
          pageSection={pageSection}
          redirectTo={redirectTo}
          routeNewUserToOnboarding={routeNewUserToOnboarding}
          resetForm={resetForm}
          postalCodeRequired={postalCodeRequired}
          enteredPostalCode={enteredPostalCode}
          enteredCountryCode={enteredCountryCode}
          venueToFollow={venueToFollow}
          attendanceData={attendanceData}
          handleAuthComplete={handleAuthComplete}
          callback={callback}
        />
      )}
    </div>
  );
}
